import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, ExternalLink } from "lucide-react";
import { BarChart, Bar as RBar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Panel, Loading, ErrorBox } from "@/components/Widgets";
import { API, useApi, nf, errMsg } from "@/lib/api";

const SUGGESTED = ["hostaway", "smoobu", "lodgify", "guesty", "krossbooking", "octorate"];

const Compare = ({ rows, k, label, suffix = "" }) => (
  <Panel title={label} testid={`chart-${k}`}>
    <div className="h-56"><ResponsiveContainer>
      <BarChart data={rows} layout="vertical" margin={{ left: 10 }}>
        <CartesianGrid stroke="#F1F1EC" horizontal={false} />
        <XAxis type="number" tickLine={false} axisLine={false} />
        <YAxis type="category" dataKey="username" width={110} tickLine={false} axisLine={false} />
        <Tooltip formatter={(v) => `${v}${suffix}`} />
        <RBar dataKey={k}>{rows.map((r) => <Cell key={r.username} fill={r.is_me ? "#C05C3D" : "#2A4B3C"} />)}</RBar>
      </BarChart>
    </ResponsiveContainer></div>
  </Panel>
);

const Row = ({ a, onRemove }) => (
  <TableRow data-testid={`competitor-row-${a.username}`} className={a.is_me ? "bg-[#FBF1ED]" : ""}>
    <TableCell><div className="font-semibold text-sm">@{a.username}{a.is_me && <span className="ml-2 text-[10px] text-[#C05C3D] border border-[#C05C3D] px-1">TU</span>}</div><div className="text-xs text-[#5C5C58] truncate max-w-[220px]">{a.biography}</div></TableCell>
    <TableCell className="font-mono-num">{nf(a.followers)}</TableCell>
    <TableCell className="font-mono-num">{nf(a.media_count)}</TableCell>
    <TableCell className="font-mono-num">{a.posts_per_week}</TableCell>
    <TableCell className="font-mono-num">{nf(a.avg_likes)}</TableCell>
    <TableCell className="font-mono-num">{nf(a.avg_comments)}</TableCell>
    <TableCell className="font-mono-num font-semibold">{nf(a.er_followers)}%</TableCell>
    <TableCell className="text-xs">{Object.entries(a.format_mix).map(([f, p]) => `${f} ${p}%`).join(" · ")}</TableCell>
    <TableCell>{!a.is_me && <button data-testid={`remove-competitor-${a.username}`} onClick={() => onRemove(a.username)}><Trash2 className="w-4 h-4 text-[#5C5C58] hover:text-[#C05C3D]" /></button>}</TableCell>
  </TableRow>
);

const Insights = ({ a }) => (
  <Panel title={`@${a.username}`} sub="Hashtag e contenuti migliori" testid={`competitor-detail-${a.username}`}>
    <div className="flex flex-wrap gap-1.5 mb-4">{a.top_hashtags.map((t) => <span key={t} className="text-xs border border-[#E8E8E4] px-2 py-0.5">#{t}</span>)}{!a.top_hashtags.length && <span className="text-xs text-[#5C5C58]">Nessun hashtag</span>}</div>
    <ul className="divide-y divide-[#E8E8E4]">{a.top_posts.map((p, i) => (
      <li key={i} className="py-2 flex justify-between gap-3 text-sm"><a href={p.permalink} target="_blank" rel="noreferrer" className="truncate hover:text-[#2A4B3C] flex items-center gap-1.5"><ExternalLink className="w-3 h-3 shrink-0" />{p.caption || "(senza testo)"}</a><span className="font-mono-num text-xs whitespace-nowrap">{p.format} · {nf(p.likes)} like</span></li>
    ))}</ul>
  </Panel>
);

export default function Competitors() {
  const { data, error, loading, reload } = useApi("/competitors");
  const [u, setU] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async (name) => {
    const v = (name ?? u).trim();
    if (!v) return;
    setBusy(true);
    try {
      await axios.post(`${API}/competitors`, { username: v });
      toast.success(`@${v.replace("@", "")} aggiunto`);
      setU("");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const remove = async (name) => {
    try {
      await axios.delete(`${API}/competitors/${name}`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (loading && !data) return <Loading />;
  if (error) return <ErrorBox msg={error} />;
  const rows = data.accounts;
  return (
    <>
      <PageHeader eyebrow="Instagram Business Discovery · ultimi 30 post" title="Confronto concorrenti" desc="Aggiungi account Instagram Business o Creator del settore ospitalità e confronta follower, frequenza, engagement e formati con i tuoi. I dati entrano anche nella Strategia AI." />
      <Panel className="mb-4" testid="add-competitor-panel">
        <form onSubmit={(e) => { e.preventDefault(); add(); }} className="flex gap-2">
          <div className="relative flex-1"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C58]">@</span>
            <Input data-testid="competitor-input" value={u} onChange={(e) => setU(e.target.value)} placeholder="username instagram, es. hostaway" className="pl-7 rounded-none h-11" /></div>
          <Button data-testid="add-competitor-btn" disabled={busy} className="rounded-none h-11 bg-[#2A4B3C] hover:bg-[#36604C] px-6">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}Aggiungi</Button>
        </form>
        <div className="flex flex-wrap gap-2 mt-4"><span className="label self-center mr-1">Suggeriti</span>
          {SUGGESTED.map((s) => <button key={s} data-testid={`suggest-competitor-${s}`} onClick={() => add(s)} className="text-xs border border-[#E8E8E4] px-2.5 py-1 hover:border-[#2A4B3C] transition-colors">@{s}</button>)}
        </div>
        {data.errors.map((e) => <p key={e.username} className="text-xs text-[#C05C3D] mt-2">@{e.username}: {e.error}</p>)}
      </Panel>
      <Panel testid="competitor-table" title="Tabella comparativa" sub="ER = (like + commenti medi) / follower" className="mb-4">
        <div className="overflow-x-auto"><Table>
          <TableHeader><TableRow>{["Account", "Follower", "Post totali", "Post/sett.", "Like medi", "Commenti medi", "ER", "Mix formati", ""].map((h) => <TableHead key={h} className="label !text-[10px]">{h}</TableHead>)}</TableRow></TableHeader>
          <TableBody>{rows.map((a) => <Row key={a.username} a={a} onRemove={remove} />)}</TableBody>
        </Table></div>
        {rows.length === 1 && <p className="text-sm text-[#5C5C58] mt-4">Aggiungi il primo concorrente per iniziare il confronto.</p>}
      </Panel>
      {rows.length > 1 && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <Compare rows={rows} k="er_followers" label="Engagement rate" suffix="%" />
            <Compare rows={rows} k="posts_per_week" label="Post a settimana" />
            <Compare rows={rows} k="avg_likes" label="Like medi per post" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">{rows.filter((a) => !a.is_me).map((a) => <Insights key={a.username} a={a} />)}</div>
        </>
      )}
    </>
  );
}
